// Drag-and-drop uploader for binary documents (PDF spec sheets, MP4 demo
// clips). Sister component to ImageUpload — same UX shell, but skips the
// image-processing pipeline and renders a filename chip instead of a
// preview thumb. Pre-existing URLs (from before R2 wiring landed) still
// load: the chip shows "Linked file" + an opens-in-new-tab link.

import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FileText, Film, Loader2, Upload, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { uploadFileToR2, type DocumentBucket } from "@/lib/r2-upload";

type AcceptKind = "pdf" | "video";

interface FileUploadProps {
  value?: string;
  onChange: (url: string) => void;
  bucket: DocumentBucket;
  /** Decides which file types the dropzone accepts + which icon renders. */
  accept: AcceptKind;
  className?: string;
  placeholder?: string;
}

const ACCEPT_MAP: Record<AcceptKind, Record<string, string[]>> = {
  pdf: { "application/pdf": [".pdf"] },
  video: {
    "video/mp4": [".mp4"],
    "video/webm": [".webm"],
    "video/quicktime": [".mov"],
  },
};

export const FileUpload: React.FC<FileUploadProps> = ({
  value,
  onChange,
  bucket,
  accept,
  className,
  placeholder,
}) => {
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      try {
        setUploading(true);
        const url = await uploadFileToR2(file, { bucket });
        onChange(url);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : typeof err === "string" ? err : "Unknown error";
        console.error("[FileUpload]", message);
        alert(`File upload failed: ${message}`);
      } finally {
        setUploading(false);
      }
    },
    [bucket, onChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT_MAP[accept],
    maxFiles: 1,
    multiple: false,
  });

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  // Pull a human-friendly filename from the stored URL — falls back to a
  // generic label for legacy/external links that don't follow our naming.
  const displayName = (() => {
    if (!value) return "";
    try {
      const u = new URL(value);
      const last = u.pathname.split("/").filter(Boolean).pop();
      return last ? decodeURIComponent(last) : value;
    } catch {
      return value;
    }
  })();

  const Icon = accept === "video" ? Film : FileText;

  return (
    <div className={cn("space-y-2", className)}>
      {value ? (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3">
          <Icon className="h-5 w-5 shrink-0 text-primary" />
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 truncate text-sm font-medium text-foreground hover:underline"
          >
            {displayName}
            <ExternalLink className="ml-1 inline h-3 w-3 align-text-top opacity-60" />
          </a>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={clear}
            aria-label="Remove file"
          >
            <X size={14} />
          </Button>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer flex flex-col items-center justify-center text-center gap-2 min-h-[140px]",
            isDragActive
              ? "border-primary bg-primary/5 scale-[0.98]"
              : "border-border hover:border-primary/50 hover:bg-muted/30",
            uploading && "opacity-50 cursor-not-allowed pointer-events-none",
          )}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
              <p className="text-sm text-foreground font-medium animate-pulse">Uploading…</p>
            </>
          ) : (
            <>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mb-1">
                {isDragActive ? (
                  <Upload className="h-5 w-5 text-primary" />
                ) : (
                  <Icon className="h-5 w-5 text-primary" />
                )}
              </div>
              <div>
                <p className="text-sm text-foreground font-medium">
                  {isDragActive
                    ? "Drop to upload"
                    : placeholder ?? (accept === "pdf" ? "Click or drag to upload PDF" : "Click or drag to upload video")}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {accept === "pdf" ? "PDF (max 50MB)" : "MP4 / WebM / MOV (max 50MB)"}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
